import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import { stcBinTypeService, StcBinTypeDto, UpdateStcBinTypeRequest } from '../../../../../services/stcBinTypeService';
import { stcBinGrpService } from '../../../../../services/stcBinGrpService';
import { stcPriorityService } from '../../../../../services/stcPriorityService';
import { stcMovementTypeService } from '../../../../../services/stcMovementTypeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { BinTypeSidePanel } from './BinTypeSidePanel';
import { BinTypeCreateModal } from './BinTypeCreateModal';

interface MasterDataLookups {
  binGroups: Map<number, { binGrpNo: number; description1?: string }>;
  priorities: Map<number, { priorityNo: number; description1?: string }>;
  movementTypes: Map<number, { movementTypeNo: number; description1?: string }>;
}

interface EnrichedBinType extends StcBinTypeDto {
  binGrpName?: string;
  pickPriorityName?: string;
  putAwayPriorityName?: string;
  movementTypeName?: string;
}

interface BinTypeDataTableProps {
  refreshTrigger?: number;
}

export const BinTypeDataTable: React.FC<BinTypeDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<EnrichedBinType[]>([]);
  const [masterData, setMasterData] = useState<MasterDataLookups | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing
  const inlineEditConfig = useInlineEdit<EnrichedBinType>({
    getRowId: (row) => row.id,
    onSave: async (_originalRow, editedData) => {
      const request: UpdateStcBinTypeRequest = {
        binTypeNo: editedData.binTypeNo as number | undefined,
        description1: editedData.description1 as string | undefined,
        binGrpNo: editedData.binGrpNo as number | undefined,
        pickPriorityNo: (editedData.pickPriorityNo as number) || 1,
        putAwayPriorityNo: (editedData.putAwayPriorityNo as number) || 1,
        pickCustomOrder: editedData.pickCustomOrder as number | undefined,
        putAwayCustomOrder: editedData.putAwayCustomOrder as number | undefined,
        movementTypeNo: editedData.movementTypeNo as number | undefined,
        reservationAllowed: (editedData.reservationAllowed as boolean) || false,
        negativeReservationAllowed: (editedData.negativeReservationAllowed as boolean) || false,
        autoReplenishment: (editedData.autoReplenishment as boolean) || false,
        pickableYesNo: (editedData.pickableYesNo as boolean) || false,
        sharedBinYesNo: editedData.sharedBinYesNo as boolean | undefined,
        sharedBinMaxQuantity: editedData.sharedBinMaxQuantity as number | undefined,
        sharedBinPreferEmptyBin: editedData.sharedBinPreferEmptyBin as boolean | undefined,
        replenishMinLevel: editedData.replenishMinLevel as number | undefined,
        replenishMaxLevel: editedData.replenishMaxLevel as number | undefined,
        replenishMaxBins: editedData.replenishMaxBins as number | undefined,
        replenishFixedBins: editedData.replenishFixedBins as boolean | undefined,
        replenishMaxBinsSource: editedData.replenishMaxBinsSource as number | undefined
      };
      await stcBinTypeService.update(
        _originalRow.companyNo,
        _originalRow.binTypeNo,
        request
      );
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || 'Feil ved lagring')
  });

  // Build dropdown options from master data
  const binGrpOptions = useMemo(() =>
    masterData ? Array.from(masterData.binGroups.values()).map(g => ({
      value: g.binGrpNo, label: `${g.binGrpNo} - ${g.description1 || ''}`
    })) : [], [masterData]);

  const priorityOptions = useMemo(() =>
    masterData ? Array.from(masterData.priorities.values()).map(p => ({
      value: p.priorityNo, label: `${p.priorityNo} - ${p.description1 || ''}`
    })) : [], [masterData]);

  const movementTypeOptions = useMemo(() =>
    masterData ? Array.from(masterData.movementTypes.values()).map(m => ({
      value: m.movementTypeNo, label: `${m.movementTypeNo} - ${m.description1 || ''}`
    })) : [], [masterData]);

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const [binTypesData, binGroupsData, prioritiesData, movementTypesData] = await Promise.all([
        stcBinTypeService.getCurrent().catch(() => stcBinTypeService.getAll().catch(() => [])),
        stcBinGrpService.getCurrent().catch(() => []),
        stcPriorityService.getCurrent().catch(() => []),
        stcMovementTypeService.getCurrent().catch(() => [])
      ]);

      const lookups: MasterDataLookups = {
        binGroups: new Map(binGroupsData.map(g => [g.binGrpNo, g])),
        priorities: new Map(prioritiesData.map(p => [p.priorityNo, p])),
        movementTypes: new Map(movementTypesData.map(m => [m.movementTypeNo, m]))
      };

      const enriched: EnrichedBinType[] = binTypesData.map(bt => ({
        ...bt,
        binGrpName: bt.binGrpNo ? lookups.binGroups.get(bt.binGrpNo)?.description1 : undefined,
        pickPriorityName: bt.pickPriorityNo ? lookups.priorities.get(bt.pickPriorityNo)?.description1 : undefined,
        putAwayPriorityName: bt.putAwayPriorityNo ? lookups.priorities.get(bt.putAwayPriorityNo)?.description1 : undefined,
        movementTypeName: bt.movementTypeNo ? lookups.movementTypes.get(bt.movementTypeNo)?.description1 : undefined
      }));

      setData(enriched);
      setMasterData(lookups);
    } catch (err: any) {
      console.error('Error loading bin type data:', err);
      setError('Kunne ikke laste lokasjonstyper. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  // Column factory
  const columnFactory = createColumnFactory<EnrichedBinType>();

  // Column definitions
  const columns: ColumnDefinition<EnrichedBinType>[] = useMemo(() => [
    columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', 'Type nr.', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description1', 'description1', 'Beskrivelse', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('binGrpNo', 'binGrpNo', 'Lok.Grp', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBinType) => item.binGrpName ? `${value} - ${item.binGrpName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: binGrpOptions
    }),

    columnFactory.createNumberColumn('pickPriorityNo', 'pickPriorityNo', 'Plukkprioritet', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBinType) => item.pickPriorityName ? `${value} - ${item.pickPriorityName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('putAwayPriorityNo', 'putAwayPriorityNo', 'Plasseringsprioritet', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBinType) => item.putAwayPriorityName ? `${value} - ${item.putAwayPriorityName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: priorityOptions
    }),

    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', 'Bevegelseskode', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      formatter: (value: any, item: EnrichedBinType) => item.movementTypeName ? `${value} - ${item.movementTypeName}` : String(value || '-'),
      editable: true,
      editType: 'select',
      editOptions: movementTypeOptions
    }),

    columnFactory.createBooleanColumn('reservationAllowed', 'reservationAllowed', 'Reserverbar', {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('negativeReservationAllowed', 'negativeReservationAllowed', 'Negativ beholdning', {
      minWidth: 150,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('pickableYesNo', 'pickableYesNo', 'Plukkbar', {
      minWidth: 100,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('autoReplenishment', 'autoReplenishment', 'Auto-påfylling', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinYesNo', 'sharedBinYesNo', 'Felleslokasjon', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('sharedBinMaxQuantity', 'sharedBinMaxQuantity', 'Maks ant. felleslok.', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('sharedBinPreferEmptyBin', 'sharedBinPreferEmptyBin', 'Prioriter tomme felleslok.', {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('pickCustomOrder', 'pickCustomOrder', 'Plukkrekkefølge', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('putAwayCustomOrder', 'putAwayCustomOrder', 'Innlagringsrekkefølge', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMinLevel', 'replenishMinLevel', 'Påfylling min %', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMaxLevel', 'replenishMaxLevel', 'Påfylling maks %', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMaxBins', 'replenishMaxBins', 'Maks påfyllingslok.', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('replenishFixedBins', 'replenishFixedBins', 'Faste påfyllingslok.', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('replenishMaxBinsSource', 'replenishMaxBinsSource', 'Maks kildelok.', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [binGrpOptions, priorityOptions, movementTypeOptions]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert('Velg minst én lokasjonstype for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length]);

  // Table actions
  const tableActions: TableAction<EnrichedBinType>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny lokasjonstype',
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedIds.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit]);

  // Build selected rows map for BinTypeSidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  // Get raw data for side panel
  const rawData: StcBinTypeDto[] = data;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'bin-types-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <BinTypeSidePanel
          selectedRows={selectedRowsMap}
          data={rawData}
          masterData={masterData}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <BinTypeCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
          masterData={masterData}
        />
      )}
    </div>
  );
};
